import React, { useState } from "react";
import {
    Users,
    Mail,
    UserPlus,
    Trash2,
    Shield,
    AlertCircle,
    Loader2,
    ChevronDown,
    Check,
    Send,
    Clock
} from "lucide-react";
import {
    inviteOrgMember,
    removeOrgMember,
    updateMemberRole,
    resendMemberInvite
} from "../../api/organizations";
import {
    ORG_ROLES,
    ORG_ROLE_LABELS,
    MEMBER_STATUS,
    MEMBER_STATUS_COLORS,
    ASSIGNABLE_ROLES,
    isLimitReached,
    getInitials,
    isValidEmail,
    canManageUser
} from "../../utils/organizationHelpers";

const RoleDropdown = ({ currentRole, onRoleChange, disabled }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="relative">
            <button
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors ${
                    disabled
                        ? "dark:bg-slate-700 bg-slate-100 dark:text-slate-400 text-slate-500 cursor-not-allowed"
                        : "dark:bg-slate-700 bg-slate-100 dark:text-slate-300 text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600"
                }`}
            >
                {ORG_ROLE_LABELS[currentRole] || currentRole}
                {!disabled && <ChevronDown className="w-3 h-3" />}
            </button>
            {isOpen && (
                <>
                    <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsOpen(false)}
                    />
                    <div className="absolute right-0 mt-1 w-32 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-lg shadow-xl z-20 py-1">
                        {ASSIGNABLE_ROLES.map((role) => (
                            <button
                                key={role}
                                onClick={() => {
                                    onRoleChange(role);
                                    setIsOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors ${
                                    role === currentRole
                                        ? "dark:bg-teal-500/10 bg-teal-50 dark:text-teal-400 text-teal-600"
                                        : "dark:text-slate-300 text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700"
                                }`}
                            >
                                {ORG_ROLE_LABELS[role]}
                                {role === currentRole && <Check className="w-4 h-4" />}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const MembersList = ({
    organization,
    workspace,
    group,
    members,
    searchQuery = "",
    limits,
    userEmail,
    onRefresh,
    showInviteForm = true,
    context = "organization",
    compact = false
}) => {
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRole, setInviteRole] = useState(ORG_ROLES.MEMBER);
    const [isInviting, setIsInviting] = useState(false);
    const [inviteError, setInviteError] = useState("");

    // Delete modal
    const [memberToDelete, setMemberToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");

    // Resend invite
    const [resendingFor, setResendingFor] = useState(null);

    const memberLimit = limits?.max_users_per_org || limits?.members || 10;
    const canInvite = !isLimitReached(members.length, memberLimit);

    const filteredMembers = members.filter((member) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        // Backend returns user_id as email
        const memberEmail = member.email || member.user_id || "";
        // Handle invalid name values
        const invalidNames = ['none', 'none none', 'null', 'undefined', ''];
        const rawName = member.name?.trim() || "";
        const memberName = invalidNames.includes(rawName.toLowerCase()) ? "" : rawName;
        return (
            memberName.toLowerCase().includes(query) ||
            memberEmail.toLowerCase().includes(query)
        );
    });

    const handleInvite = async (e) => {
        e.preventDefault();
        const emailToInvite = inviteEmail.trim();

        if (!emailToInvite) return;

        if (!isValidEmail(emailToInvite)) {
            setInviteError("Please enter a valid email address.");
            return;
        }

        if (emailToInvite.toLowerCase() === userEmail?.toLowerCase()) {
            setInviteError("You cannot invite yourself.");
            return;
        }

        if (members.some(m => m.email?.toLowerCase() === emailToInvite.toLowerCase())) {
            setInviteError("This user is already a member.");
            return;
        }

        if (!canInvite) {
            setInviteError("Member limit reached.");
            return;
        }

        setIsInviting(true);
        setInviteError("");
        try {
            // Backend expects { email, role_id }
            await inviteOrgMember(organization.id, {
                email: emailToInvite,
                role_id: inviteRole
            });
            setInviteEmail("");
            setInviteRole(ORG_ROLES.MEMBER);
            await onRefresh?.();
        } catch (err) {
            setInviteError(err?.message || "Failed to invite member.");
        } finally {
            setIsInviting(false);
        }
    };

    const handleRemoveMember = async () => {
        if (!memberToDelete) return;
        setIsDeleting(true);
        setDeleteError("");
        try {
            // Backend expects user_id (email)
            const memberId = memberToDelete.user_id || memberToDelete.email || memberToDelete.id;
            await removeOrgMember(organization.id, memberId);
            setMemberToDelete(null);
            await onRefresh?.();
        } catch (err) {
            setDeleteError(err?.message || "Failed to remove member.");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleRoleChange = async (memberId, newRole) => {
        try {
            // Backend expects { role_id }
            await updateMemberRole(organization.id, memberId, { role_id: newRole });
            await onRefresh?.();
        } catch (err) {
            console.error("Failed to update role:", err);
        }
    };

    const handleResendInvite = async (member) => {
        const memberId = member.id || member.user_id || member.email;
        setResendingFor(memberId);
        try {
            await resendMemberInvite(organization.id, memberId);
            // Could show a success toast here
        } catch (err) {
            console.error("Failed to resend invite:", err);
        } finally {
            setResendingFor(null);
        }
    };

    // Determine current user's role to restrict actions
    // Backend returns user_id as email
    const currentUserMember = members.find(m =>
        (m.email || m.user_id || "").toLowerCase() === userEmail?.toLowerCase()
    );
    const currentUserRole = currentUserMember?.role_id || currentUserMember?.role || ORG_ROLES.MEMBER;

    return (
        <div className="space-y-4">
            {/* Invite Form */}
            {showInviteForm && (
                <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                        <div>
                            <h3 className="text-lg font-semibold dark:text-white text-slate-900">
                                Organization Members
                            </h3>
                            <div className="flex items-center gap-3 mt-1">
                                <p className="text-sm dark:text-slate-400 text-slate-500">
                                    {members.length} of {memberLimit} seats used
                                </p>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    memberLimit - members.length > 0
                                        ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
                                        : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                                }`}>
                                    {memberLimit - members.length > 0
                                        ? `${memberLimit - members.length} remaining`
                                        : "No seats left"
                                    }
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm px-3 py-1.5 dark:bg-slate-800 bg-slate-100 border dark:border-transparent border-slate-200 rounded-lg w-fit">
                            <Shield className="w-4 h-4 text-teal-500" />
                            <span className="dark:text-slate-300 text-slate-600">
                                Your role: <span className="font-semibold dark:text-white text-slate-900 capitalize">{ORG_ROLE_LABELS[currentUserRole]}</span>
                            </span>
                        </div>
                    </div>

                    {canInvite ? (
                        <form onSubmit={handleInvite} className="flex flex-wrap gap-2">
                            <div className="relative flex-1 min-w-[200px]">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                <input
                                    type="email"
                                    placeholder="colleague@example.com"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 dark:bg-slate-950 bg-slate-50 border dark:border-slate-700 border-slate-300 rounded-lg text-sm dark:text-white text-slate-900 focus:ring-1 focus:ring-teal-500 outline-none"
                                    required
                                />
                            </div>
                            <select
                                value={inviteRole}
                                onChange={(e) => setInviteRole(e.target.value)}
                                className="px-3 py-2 dark:bg-slate-950 bg-slate-50 border dark:border-slate-700 border-slate-300 rounded-lg text-sm dark:text-white text-slate-900 focus:ring-1 focus:ring-teal-500 outline-none"
                            >
                                {ASSIGNABLE_ROLES.map((role) => (
                                    <option key={role} value={role}>
                                        {ORG_ROLE_LABELS[role]}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="submit"
                                disabled={isInviting || !inviteEmail}
                                className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isInviting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <UserPlus className="w-4 h-4" />
                                )}
                                Invite
                            </button>
                        </form>
                    ) : (
                        <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2 text-sm text-amber-400">
                            <AlertCircle className="w-4 h-4" />
                            Member limit reached. Upgrade your plan to add more members.
                        </div>
                    )}

                    {inviteError && (
                        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-400">
                            <AlertCircle className="w-4 h-4" />
                            {inviteError}
                        </div>
                    )}
                </div>
            )}

            {/* Members Table */}
            {filteredMembers.length === 0 ? (
                <div className="text-center py-12 dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl">
                    <Users className="w-12 h-12 mx-auto mb-3 dark:text-slate-600 text-slate-300" />
                    <p className="dark:text-slate-400 text-slate-500">
                        {searchQuery ? "No members match your search" : "No members yet"}
                    </p>
                </div>
            ) : (
                <div className={`overflow-hidden rounded-lg border dark:border-slate-800 border-slate-200 ${compact ? "" : "dark:bg-slate-900/50 bg-white"}`}>
                    <table className="w-full text-left text-sm">
                        <thead className="dark:bg-slate-900 bg-slate-50 dark:text-slate-400 text-slate-500 border-b dark:border-slate-800 border-slate-200">
                            <tr>
                                <th className="px-4 py-3 font-semibold">Member</th>
                                <th className="px-4 py-3 font-semibold">Role</th>
                                <th className="px-4 py-3 font-semibold">Status</th>
                                <th className="px-4 py-3 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y dark:divide-slate-800 divide-slate-100 dark:bg-slate-900/50 bg-white">
                            {filteredMembers.map((member, index) => {
                                // Backend returns user_id as email
                                const memberEmail = member.email || member.user_id || "";
                                // Check for invalid name values (Python None converted to string, etc.)
                                const invalidNames = ['none', 'none none', 'null', 'undefined', ''];
                                const rawName = member.name?.trim();
                                const isValidName = rawName && !invalidNames.includes(rawName.toLowerCase());
                                const memberName = isValidName ? rawName : (memberEmail.split('@')[0] || "Unknown");
                                const isCurrentUser = memberEmail.toLowerCase() === userEmail?.toLowerCase();
                                // Backend uses role_id, frontend uses role
                                const memberRole = member.role_id || member.role;
                                const isOwner = memberRole === ORG_ROLES.OWNER;
                                const canManage = canManageUser(currentUserRole, memberRole) && !isCurrentUser;
                                const statusColors = MEMBER_STATUS_COLORS[member.status] || MEMBER_STATUS_COLORS[MEMBER_STATUS.PENDING];
                                // Use user_id (email) as key since that's what backend returns
                                const memberKey = member.user_id || member.email || member.id || `member-${index}`;

                                return (
                                    <tr
                                        key={memberKey}
                                        className="group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-xs font-bold text-white">
                                                    {getInitials(memberName || memberEmail)}
                                                </div>
                                                <div>
                                                    <p className="dark:text-white text-slate-900 font-medium">
                                                        {memberName}
                                                        {isCurrentUser && (
                                                            <span className="ml-2 text-xs dark:text-teal-400 text-teal-600">(You)</span>
                                                        )}
                                                    </p>
                                                    <p className="text-xs dark:text-slate-400 text-slate-500">
                                                        {memberEmail}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {isOwner ? (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium dark:bg-amber-500/10 bg-amber-50 dark:text-amber-400 text-amber-600">
                                                    <Shield className="w-3 h-3" />
                                                    Owner
                                                </span>
                                            ) : (
                                                <RoleDropdown
                                                    currentRole={memberRole}
                                                    onRoleChange={(newRole) => handleRoleChange(member.user_id || member.email, newRole)}
                                                    disabled={!canManage}
                                                />
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusColors.bg} ${statusColors.text} border ${statusColors.border}`}>
                                                    {member.status === 'invited' ? (
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            Pending
                                                        </span>
                                                    ) : (
                                                        member.status || MEMBER_STATUS.PENDING
                                                    )}
                                                </span>
                                                {(member.status === 'invited' || member.status === 'pending') && canManage && (
                                                    <button
                                                        onClick={() => handleResendInvite(member)}
                                                        disabled={resendingFor === (member.id || member.user_id || member.email)}
                                                        className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-sky-600 dark:text-sky-400 hover:bg-sky-100 dark:hover:bg-sky-900/30 rounded transition-colors disabled:opacity-50"
                                                        title="Resend invitation"
                                                    >
                                                        {resendingFor === (member.id || member.user_id || member.email) ? (
                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                        ) : (
                                                            <Send className="w-3 h-3" />
                                                        )}
                                                        Resend
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {canManage && (
                                                <button
                                                    onClick={() => setMemberToDelete(member)}
                                                    className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                                                    title="Remove member"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {memberToDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-sm shadow-2xl p-6">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-12 h-12 bg-red-500/10 text-red-400 rounded-full flex items-center justify-center">
                                <Trash2 className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold dark:text-white text-slate-900">Remove Member?</h3>
                                <p className="text-sm dark:text-slate-400 text-slate-500 mt-2">
                                    Are you sure you want to remove <span className="font-semibold dark:text-white text-slate-900">{memberToDelete?.email}</span>?
                                    They will lose access to the organization.
                                </p>
                            </div>
                            {deleteError && (
                                <div className="w-full p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-400">
                                    <AlertCircle className="w-4 h-4" />
                                    {deleteError}
                                </div>
                            )}
                            <div className="flex gap-3 w-full mt-2">
                                <button
                                    onClick={() => {
                                        setMemberToDelete(null);
                                        setDeleteError("");
                                    }}
                                    className="flex-1 px-4 py-2 dark:bg-slate-800 bg-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 dark:text-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleRemoveMember}
                                    disabled={isDeleting}
                                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isDeleting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        "Remove"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MembersList;
